import moment from 'moment';
import lodash from 'lodash';

/**
 * Enum for date time formats and placeholders.
 *
 * @readonly
 * @enum {string}
 */
export const DATE_TIME = {
    DATE_ONLY_PLACEHOLDER: 'DD MMM YYYY',
    DATE_ONLY_FORMAT: 'DD MMM YYYY',
    DATE_TIME_PLACEHOLDER: 'DD MMM YYYY hh:mm A',
    DATE_TIME_FORMAT: 'DD MMM YYYY hh:mm A',
};

export const DEFAULT_DATETIME_INPUT_FORMAT = 'ddd MMM DD YYYY hh:mm:ssZ';

export function verifyInputFormat(value) {
    const momentValue = moment(value, DEFAULT_DATETIME_INPUT_FORMAT);
    return momentValue.isValid() ? DEFAULT_DATETIME_INPUT_FORMAT : null;
}

/**
 * Mutates DATEONLY input values and appends the current time to the date
 * @param value
 * @param dataType
 * @returns moment object
 *
 * NOTE: This method compensates for the bug with react-widget datetimepicker component,
 * which does not provide current time with the date object and instead returns start of the day.
 */
export function mutateInputWithCurrentTime(value, dataType) {
    if (lodash.isNil(dataType) || 'DATEONLY' !== dataType.toUpperCase()) {
        return value;
    }

    let momentValue = moment(value);
    if (!momentValue.isValid()) {
        return value;
    }

    const currentDateTime = new Date();
    const mutatedValue = momentValue
        .hours(currentDateTime.getHours())
        .minutes(currentDateTime.getMinutes())
        .seconds(currentDateTime.getSeconds())
        .milliseconds(currentDateTime.getMilliseconds());

    return mutatedValue;
}

export const getHoursMinutesForCurrentTime = () => getHoursMinutesFromDate(moment());

export const getHoursMinutesFromDate = (date) => date.format('HH:mm');

export const applyTimeZoneToDate = (dateTime, timezoneOffset) =>
    moment(dateTime, DEFAULT_DATETIME_INPUT_FORMAT).utcOffset(timezoneOffset);

export const daysBetweenDatesUTC = (dt1, dt2) => {
    const dayCount1 = Math.floor(moment.duration(moment(dt1).utc()).asDays());
    const dayCount2 = Math.floor(moment.duration(moment(dt2).utc()).asDays());
    const dayDelta = (dayCount1 - dayCount2);
    // make sure that on the same day, the diff is 1
    return  dayDelta < 0 ? dayDelta : dayDelta + 1;
};

// this function needs a redux store in order to get institution tz
export const daysBetweenDatesLocal = (dt1, dt2, institutionOffset) => {
    const m1 = moment(dt1).utcOffset(institutionOffset);
    const m2 = moment(dt2).utcOffset(institutionOffset);
    // we just care about the dates
    const date1 = moment(m1.format('YYYY-MM-DD'));
    const date2 = moment(m2.format('YYYY-MM-DD'));
    const dayDelta = date2.diff(date1,'days' );
    // make sure that on the same day, the diff is 1
    return  dayDelta < 0 ? dayDelta : dayDelta + 1;
};

export const daysSinceNowUTC = (dt) => Math.abs(daysBetweenDatesUTC(dt, moment()));

export const daysSinceDateLocal = (dt, institutionOffset) => Math.abs(daysBetweenDatesLocal(dt, moment(), institutionOffset));

export const daysSinceToday = (dt) => Math.abs(daysBetweenDatesUTC(moment(),dt));

export const replaceTimezone = (date, toTz = null) => {
    if (lodash.isNil(date)) {
        return null;
    }

    let dateString = lodash.isString(date) ? date : '';
    if (lodash.isDate(date)) {
        dateString = toISOLocalWithTz(date);
    }

    let targetTimezone = toTz;
    if (lodash.isNil(toTz)) {
        targetTimezone = moment().format('Z');
    }

    const timezoneRegex = new RegExp(/([+-][0-1][0-9]:[0-5][0-9])|Z/);
    const output = lodash.replace(dateString, timezoneRegex, targetTimezone);
    return output ? moment(output).toDate() : null;
};

export const toISOLocalWithTz = (date, tz = null) => {
    if (!lodash.isDate(date)) {
        return null;
    }

    let tzOffset = tz;
    if (lodash.isNil(tz)) {
        tzOffset = moment().format('Z');
    }

    return moment(date).utcOffset(tzOffset).format('YYYY-MM-DD[T]HH:mm:ssZ');
};

export const getDateNoTime = (date) => {
    if (!date) {
        return null;
    }

    const momentDate = moment.utc(date);
    return new Date(momentDate.year(), momentDate.month(), momentDate.date());
};

export const getLocalDateNoTime = (date) => {
    if (!date) {
        return null;
    }

    const momentDate = moment(date);
    return new Date(momentDate.year(), momentDate.month(), momentDate.date());
};

export const convertToISOString = (date) => {
    if (lodash.isNil(date)) {
        return null;
    }

    return moment(date).toISOString();
};

export const formatDateAndTimeForSave = (date, time, timezone) => {
    if (date && time) {
        const formattedDate = moment(date).format('L');
        const dateTime = moment(`${formattedDate} ${time}`);
        return `${dateTime.format().slice(0, 19)}${timezone}`;
    }

    if (date && !time) {
        const formattedDate = moment(date).format('L');
        const dateTime = moment(`${formattedDate} 23:59:59`);
        return `${dateTime.format().slice(0, 19)}${timezone}`;
    }

    return `9999-12-30T23:59:59${timezone}`;
};

// timezone != offset!
// timezone here is expected to be a value like 'US/Eastern'
// https://stackoverflow.com/tags/timezone/info
// https://momentjs.com/timezone/docs/#/using-timezones/formatting/
export const getTzAbbreviation = (date, timezone) => {
    return moment.tz(date, timezone).format('z');
};
